import { type ReactNode } from "react";
import { Ai21LabsWordmark } from "../../assets/logos";

/* The shared left brand panel for SplitScreenLayout — used by both the auth
 * screens and the onboarding wizard so the two stay identical (single source
 * of truth). Per the Figma guideline (OPSS-22) it sits transparently on the
 * frame's photo ground: the ai21 · Gateway lockup at the top, an optional
 * `visual` (the consumers pass the animated Orb) above a white, vertically
 * centered hero, and a © footer. `visual` is passed in rather than imported
 * so this DS component stays free of app-level dependencies. The copy
 * defaults to the Gateway's; a sibling app (Harness Optimizer) passes its
 * own so the frame stays one component. */
export interface BrandPanelProps {
  visual?: ReactNode;
  /** The bracketed product label beside the ai21 wordmark. */
  label?: string;
  title?: string;
  subtitle?: string;
}

export function BrandPanel({
  visual,
  label = "Gateway",
  title = "Cut your wasted tokens",
  subtitle = "Reduce your agent costs without harming quality.",
}: BrandPanelProps = {}) {
  return (
    <>
      <span className="inline-flex items-center gap-2">
        <Ai21LabsWordmark className="h-[19px] text-[color:var(--fg-on-dark)]" />
        {/* The label word is full white; only the brackets are dimmed. */}
        <span
          className="t-mono inline-flex items-center gap-1"
          style={{
            color: "var(--fg-on-dark)",
            fontSize: "13px",
            letterSpacing: "0.01em",
          }}
        >
          <span style={{ color: "rgba(255,255,255,0.4)" }}>[</span>
          {label}
          <span style={{ color: "rgba(255,255,255,0.4)" }}>]</span>
        </span>
      </span>

      {/* Anchored toward the bottom (not centered), per the Figma guideline:
          the hero sits in the lower half with a fixed gap above the footer. */}
      <div className="mt-auto mb-[14vh] pt-16">
        {visual ? (
          <div className="mb-7 flex justify-start">{visual}</div>
        ) : null}
        <h1
          className="t-h1"
          style={{
            color: "#ffffff",
            fontWeight: 300,
            lineHeight: 1.1,
            letterSpacing: "-0.75px",
            maxWidth: "340px",
            marginBottom: "24px",
          }}
        >
          {title}
        </h1>
        <p
          className="max-w-[300px] text-[18px]"
          style={{ color: "#ffffff", lineHeight: 1.4 }}
        >
          {subtitle}
        </p>
      </div>

      <p className="text-[12px]" style={{ color: "rgba(255,255,255,0.35)" }}>
        © 2026 AI21 Labs
      </p>
    </>
  );
}
